import * as React from "react";

import { useDebouncedCallback } from "./use-debounced-callback";

/**
 * Similar to `useState` but it also returns a debounced state value. This is
 * useful if you need both an immediate state update, but you also want a
 * debounced state value for things like search queries where the filtered
 * results should be delayed while the user is typing.
 *
 * @param initialValue - initial state value
 * @param timeout - debounce timeout in ms (default 300)
 * @param onDebounceSettled - callback fired when the debounced state settles
 * (after the timeout elapses). Useful for triggering side effects like saving
 * to a server when the user stops typing.
 *
 * @returns A tuple of [state, debouncedState, setState]
 */
export function useDebouncedState<T>(
  initialValue: T | (() => T),
  timeout = 300,
  onDebounceSettled?: (value: T) => void,
): [State: T, DebouncedState: T, SetState: (value: T) => void] {
  const [currentState, setCurrentState] = React.useState(initialValue);
  const [debouncedState, setDebouncedState] = React.useState(currentState);

  const debouncedSetState = useDebouncedCallback((value: T) => {
    setDebouncedState(value);
    onDebounceSettled?.(value);
  }, timeout);

  const setCurrentAndDebouncedState = React.useCallback(
    (value: T) => {
      setCurrentState(value);
      debouncedSetState(value);
    },
    [debouncedSetState],
  );

  return [currentState, debouncedState, setCurrentAndDebouncedState];
}
